import React, { useMemo, useState } from 'react';
import { getStatusDisplayName, getStatusColor, getStatusIcon } from '../constants/statusTypes';
import { useMetrics } from '../contexts/MetricsContext';
import EditLogEntryModal from './EditLogEntryModal';
import DeleteLogEntryModal from './DeleteLogEntryModal';
import StatusTimeline from './StatusTimeline';
import MoreMenu from './MoreMenu';
import './MetricDetail.css';

const MetricDetail = ({ metric, onBack, onEdit, onDelete, onLog }) => {
  const { name, type, unit, statusConfig } = metric;
  const { logEntries: allLogEntries, loading, error, updateLogEntry, deleteLogEntrySilent, refreshLogEntries } = useMetrics();
  const [editingEntry, setEditingEntry] = useState(null);
  const [deletingEntry, setDeletingEntry] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Filter log entries for this specific metric
  const logEntries = useMemo(() => {
    if (!metric?.id || !allLogEntries) return [];
    return allLogEntries.filter(entry => entry.metricId === metric.id);
  }, [allLogEntries, metric?.id]);

  const renderMetricContent = () => {
    if (type === 'numeric') {
      const formatCondition = (config, statusName) => {
        const metricNameLower = (name || 'metric').toLowerCase();
        const unitLower = (unit || 'unit').toLowerCase();
        
        if (config.operator === 'range') {
          return `If ${metricNameLower} is between ${config.min} and ${config.max} ${unitLower}`;
        } else {
          const operatorText = {
            '=': 'equal to',
            '>=': 'greater than or equal to',
            '<=': 'less than or equal to',
            '>': 'greater than',
            '<': 'less than'
          }[config.operator] || config.operator;
          return `If ${metricNameLower} is ${operatorText} ${config.value} ${unitLower}`;
        }
      };

      return (
        <div className="metric-details">
          <div className="metric-type">Numeric</div>
          <div className="metric-unit">Unit: {unit}</div>
          <div className="options-list">
            <div className="option-item">
              <span className="option-name">{formatCondition(statusConfig.operational, 'Operational')}</span>
              <span 
                className="option-status"
                style={{ color: getStatusColor('operational') }}
              >
                {getStatusIcon('operational')} {getStatusDisplayName('operational')}
              </span>
            </div>
            <div className="option-item">
              <span className="option-name">{formatCondition(statusConfig.degraded, 'Degraded')}</span>
              <span 
                className="option-status"
                style={{ color: getStatusColor('degraded') }}
              >
                {getStatusIcon('degraded')} {getStatusDisplayName('degraded')}
              </span>
            </div>
            <div className="option-item">
              <span className="option-name">{formatCondition(statusConfig.outage, 'Outage')}</span>
              <span 
                className="option-status"
                style={{ color: getStatusColor('outage') }}
              >
                {getStatusIcon('outage')} {getStatusDisplayName('outage')}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (type === 'list') {
      return (
        <div className="metric-details">
          <div className="metric-type">List / Custom Options</div>
          <div className="options-list">
            {statusConfig.options.map((option, index) => (
              <div key={index} className="option-item">
                <span className="option-name">{option.name}</span>
                <span 
                  className="option-status"
                  style={{ color: getStatusColor(option.status) }}
                >
                  {getStatusIcon(option.status)} {getStatusDisplayName(option.status)}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  const renderLogEntries = () => {
    if (loading) {
      return (
        <div className="log-entries-section">
          <h3 className="log-entries-title">Entries</h3>
          <div className="log-entries-loading">Loading entries...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="log-entries-section">
          <h3 className="log-entries-title">Entries</h3>
          <div className="log-entries-error">Error loading entries: {error}</div>
        </div>
      );
    }

    if (logEntries.length === 0) {
      return (
        <div className="log-entries-section">
          <h3 className="log-entries-title">Entries</h3>
          <div className="log-entries-empty">No entries yet. Click "Log" to add your first entry.</div>
        </div>
      );
    }

    return (
      <div className="log-entries-section">
        <h3 className="log-entries-title">Entries ({logEntries.length})</h3>
        <div className="log-entries-list">
          {logEntries.map((entry) => (
            <div key={entry.id} className="log-entry">
              <div className="log-entry-header">
                <span className="log-entry-date">
                  {new Date(entry.date).toLocaleDateString('en-US', {
                    weekday: 'short',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </span>
                <span 
                  className="log-entry-status"
                  style={{ color: getStatusColor(entry.status) }}
                >
                  {getStatusIcon(entry.status)} {getStatusDisplayName(entry.status)}
                </span>
              </div>
              <div className="log-entry-content">
                <div className="log-entry-value">
                  {type === 'numeric' ? `${entry.value} ${unit}` : entry.value}
                </div>
                {entry.notes && (
                  <div className="log-entry-notes">{entry.notes}</div>
                )}
              </div>
              <div className="log-entry-actions">
                <MoreMenu
                  onEdit={() => handleEditEntry(entry)}
                  onDelete={() => handleDeleteEntry(entry)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleLogClick = () => {
    onLog(metric);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setIsEditModalOpen(true);
  };

  const handleDeleteEntry = (entry) => {
    setDeletingEntry(entry);
    setIsDeleteModalOpen(true);
  };

  const handleSaveEdit = async (entryId, updatedData) => {
    try {
      await updateLogEntry(entryId, updatedData);
      setIsEditModalOpen(false);
      setEditingEntry(null);
    } catch (error) {
      console.error('Error updating log entry:', error);
      throw error;
    }
  };

  const handleConfirmDelete = async (entryId) => {
    try {
      await deleteLogEntrySilent(entryId);
      // Close modal first
      setIsDeleteModalOpen(false);
      setDeletingEntry(null);
      // Then refresh the data to update the UI
      await refreshLogEntries();
    } catch (error) {
      console.error('Error deleting log entry:', error);
      throw error;
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingEntry(null);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingEntry(null);
  };

  return (
    <div className="metric-detail">
      <div className="metric-detail-header">
        <button 
          className="back-btn"
          onClick={onBack}
          title="Back to metrics"
        >
          ← Back
        </button>
        <h2 className="metric-detail-title">{name}</h2>
        <div className="metric-detail-actions">
          <button 
            className="log-btn"
            onClick={handleLogClick}
            title="Log entry for this metric"
          >
            Log
          </button>
          <MoreMenu 
            onEdit={() => onEdit(metric)}
            onDelete={() => onDelete(metric)}
          />
        </div>
      </div>
      {renderMetricContent()}
      <StatusTimeline logEntries={logEntries} metric={metric} />
      {renderLogEntries()}
      
      <EditLogEntryModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleSaveEdit}
        entry={editingEntry}
        metric={metric}
      />
      
      <DeleteLogEntryModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        entry={deletingEntry}
        metric={metric}
      />
    </div>
  );
};

export default MetricDetail;
